import React, { useEffect, useMemo, useState } from 'react'
import { BiCreditCard, BiBarcodeReader } from 'react-icons/bi'
import { MdKeyboardArrowDown, MdOutlineAddCard } from 'react-icons/md'
import CardAddForm, { CardFormData } from './components/CardAddForm'
import { WarningBanners } from '../resumeStep/WarningBanners'
import { usePrepareCart } from '../../../../../hooks/usePrepareCart'
import { useFormContext } from 'react-hook-form'
import api from '../../../../../services/api'
import { listSavedCards } from '../../../../../services/cards'

interface CardBoletoPaymentProps {
  selectedMethod: string
  cartItems: any[]
  shippingAddress?: any
}

export const CardBoletoPayment: React.FC<CardBoletoPaymentProps> = ({
  selectedMethod: _selectedMethod,
  cartItems: _cartItems,
  shippingAddress
}) => {
  type SavedCardUI = {
    id: number
    external_id: string
    brand: string
    type: string
    last4: string
    holder: string
  }
  const [cards, setCards] = useState<SavedCardUI[]>([])
  const [selectedCardExternalId, setSelectedCardExternalId] = useState<string>('')
  const [showFormCard, setShowFormCard] = useState(false)
  const [percentageCard, setPercentageCard] = useState(50)
  const [percentageBoleto, setPercentageBoleto] = useState(50)
  const [installments, setInstallments] = useState<
    Array<{ id: number; installment: string; amount: number; fees: string; total: number }>
  >([])
  const [loadingInstallments, setLoadingInstallments] = useState(false)
  const [selectedInstallmentId, setSelectedInstallmentId] = useState<number | null>(null)
  const { lastOrderCreated } = usePrepareCart()
  const form = useFormContext<{ cvv_card_boleto: string }>()

  const handleAddCard = (_data: CardFormData) => {
    setShowFormCard(false)
  }

  const persistSelection = (
    partial: Partial<{
      selectedCardExternalId: string
      installments: number
      amountCard: number
      amountBoleto: number
    }>
  ) => {
    try {
      const raw = localStorage.getItem('@CANNECT:CARD_BOLETO_SELECTION')
      const prev = raw ? JSON.parse(raw) : {}
      const next = { ...prev, ...partial }
      localStorage.setItem('@CANNECT:CARD_BOLETO_SELECTION', JSON.stringify(next))
      try {
        window.dispatchEvent(new CustomEvent('card_boleto_selection_updated'))
      } catch {
        /* ignore */
      }
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    const reloadCards = async () => {
      try {
        const fetched = await listSavedCards()
        const mapped: SavedCardUI[] = fetched.map((c: any) => ({
          id: c.id,
          external_id: c.external_id || String(c.id),
          brand: c.brand || c.type || 'Cartão',
          type: c.brand || c.type || 'Cartão',
          last4: c.last4 || c.last_digits || '0000',
          holder: c.holder_name || c.holder || ''
        }))
        setCards(mapped)
        if (!selectedCardExternalId && mapped[0]?.external_id) {
          setSelectedCardExternalId(mapped[0].external_id)
          persistSelection({ selectedCardExternalId: mapped[0].external_id })
        }
      } catch {
        /* ignore */
      }
    }
    reloadCards()
  }, [])

  const validItems = useMemo(() => (_cartItems || []).filter((it: any) => (it?.quantity || 0) > 0), [_cartItems])
  const subtotal = useMemo(
    () => validItems.reduce((acc: number, item: any) => acc + parseFloat(item.amount || '0'), 0),
    [validItems]
  )
  const productDiscounts = useMemo(() => {
    return validItems.reduce((acc: number, item: any) => {
      const itemDiscount = parseFloat(item.discount || '0')
      if (itemDiscount === 0 && item.product?.price && item.product?.real_price) {
        const originalPrice = item.product.price * (item.quantity || 0)
        const finalPrice = item.product.real_price * (item.quantity || 0)
        const calculatedDiscount = Math.max(0, originalPrice - finalPrice)
        return acc + calculatedDiscount
      }
      return acc + itemDiscount
    }, 0)
  }, [validItems])
  const importCost = 145
  const freeShippingThreshold = 2300
  const remainingForFreeShipping = Math.max(0, freeShippingThreshold - (subtotal + importCost - productDiscounts))
  const importDiscount = remainingForFreeShipping <= 0 ? importCost : 0
  const computedTotal = subtotal + importCost - productDiscounts - importDiscount
  const totalAmount = (lastOrderCreated?.amount as number) || computedTotal
  const amountCardBRL = useMemo(() => (totalAmount * percentageCard) / 100, [totalAmount, percentageCard])
  const selectedInstallment = useMemo(
    () => installments.find((i) => i.id === selectedInstallmentId),
    [installments, selectedInstallmentId]
  )
  const cardFeesFactor = useMemo(() => {
    if (!selectedInstallment || totalAmount <= 0) return 1
    const total = Number(selectedInstallment.total)
    return total > 0 ? total / Number(totalAmount) : 1
  }, [selectedInstallment, totalAmount])
  const amountBoletoBRL = useMemo(() => Math.max(0, totalAmount - amountCardBRL), [totalAmount, amountCardBRL])
  const formatBRL = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)

  useEffect(() => {
    const fetchInstallments = async () => {
      try {
        setLoadingInstallments(true)
        const { data } = await api.get(`/payments/installments/${lastOrderCreated?.id}`)
        const list = Array.isArray(data?.installments) ? data.installments : []
        setInstallments(list)
        // Limpa a seleção anterior para forçar o usuário a selecionar novamente
        localStorage.removeItem('@CANNECT:CARD_BOLETO_SELECTION')
      } catch {
        setInstallments([])
      } finally {
        setLoadingInstallments(false)
      }
    }
    if (lastOrderCreated?.id) fetchInstallments()
  }, [lastOrderCreated?.id])

  return (
    <div className="h-fit w-full rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
      <div className="space-y-6">
        {/* Cabeçalho */}
        <div>
          <div className="mb-2 flex items-center gap-2">
            <div className="flex items-center gap-1 text-primary-700">
              <BiCreditCard className="h-4 w-4" />
              <span className="text-sm">+</span>
              <BiBarcodeReader className="h-4 w-4" />
            </div>
            <h2 className="text-lg font-semibold text-primary-700">Pagamento Cartão + Boleto</h2>
          </div>
          <p className="text-sm text-neutral-800">Claro! Selecione um cartão para efetuar o pagamento.</p>
        </div>

        {/* Cartão */}
        <div className="space-y-6 rounded-lg border border-gray-100 bg-gray-50 p-6">
          <h3 className="flex items-center gap-2 text-base font-semibold text-neutral-800">
            <BiCreditCard className="text-lg" />
            Cartão
          </h3>
          <hr
            style={{ borderTop: '0.2px solid #E3E3E3', borderLeft: 'none', borderRight: 'none', borderBottom: 'none' }}
          />

          {/* Porcentagem Cartão */}
          <div>
            <span className="mb-2 block text-sm text-neutral-800">Porcentagem do cartão</span>
            <div className="flex flex-wrap items-center gap-8">
              {[10, 25, 50, 75, 90].map((val) => (
                <button
                  key={val}
                  onClick={() => {
                    setPercentageCard(val)
                    setPercentageBoleto(100 - val)
                  }}
                  style={{ border: '1px solid #A2B156' }}
                  className={`inline-flex h-6 w-15 items-center justify-center gap-2.5 rounded-3xl px-4 py-1 text-base ${
                    percentageCard === val ? 'bg-[#A2B156] text-white' : 'bg-white text-[#A2B156] hover:bg-lime-100'
                  }`}>
                  {val}%
                </button>
              ))}
              <div
                style={{ border: '1px solid var(--Neutrals-Neutrals200, #CCCBCB)' }}
                className="ml-2 inline-flex h-6 w-23 items-center justify-center gap-2.5 rounded-3xl bg-[var(--Secondary-Secondary100,#EDECE7)] px-4 py-[6px] text-base text-neutral-500">
                {formatBRL(amountCardBRL)}
              </div>
            </div>
          </div>

          {/* Cartão e CVV */}
          <div className="flex flex-wrap items-center gap-4">
            <span className="flex items-center gap-1 text-sm font-semibold text-neutral-800">
              <BiCreditCard className="h-4 w-4 text-neutral-800" />
              Cartão:
            </span>

            <div className="relative flex-1">
              <select
                style={{ border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)' }}
                className="h-11 w-full appearance-none rounded-md bg-white py-2 pl-10 pr-8 text-sm text-gray-700 shadow-sm focus:outline-none"
                value={selectedCardExternalId}
                onChange={(e) => {
                  setSelectedCardExternalId(e.target.value)
                  persistSelection({ selectedCardExternalId: e.target.value })
                }}>
                {cards.map((card) => (
                  <option
                    key={card.id}
                    value={card.external_id}>{`${card.brand || card.type} **** ${card.last4}`}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute left-2 top-2.5">
                <BiCreditCard className="text-xl text-blue-600" />
              </div>
              <MdKeyboardArrowDown className="pointer-events-none absolute right-2 top-1/2 h-6 w-5 -translate-y-1/2 text-gray-500" />
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-neutral-500">
                CVV<span className="text-red-500">*</span>
              </span>
              <input
                id="cvv-input-card"
                aria-label="CVV"
                style={{ border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)' }}
                className="h-11 w-22 rounded-md bg-gray-100 px-2 py-1 text-sm text-gray-700 outline-none"
                placeholder="Ex. 123"
                maxLength={3}
                {...(form?.register ? form.register('cvv_card_boleto', { required: true, pattern: /^\d{3}$/u }) : {})}
              />
              <button
                onClick={() => setShowFormCard(true)}
                className="flex items-center border-0 bg-transparent text-xs font-medium text-primary-800 hover:underline">
                <MdOutlineAddCard className="mr-1 h-4 w-4 text-primary-800" />
                Adicionar
              </button>
            </div>

            {/* Parcelas */}
            <div className="flex w-full items-center gap-7">
              <span className="whitespace-nowrap text-sm text-gray-700">Parcelas</span>
              <div className="relative flex-1">
                <select
                  id="installments-select"
                  aria-label="Parcelas"
                  style={{ border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)' }}
                  className="h-9 w-full appearance-none rounded-md bg-white px-4 pr-8 text-sm text-gray-700 shadow-sm focus:outline-none"
                  value={selectedInstallmentId || ''}
                  disabled={loadingInstallments}
                  onChange={(e) => {
                    const value = e.target.value
                    if (value === '') {
                      setSelectedInstallmentId(null)
                      return
                    }
                    const inst = Number(value)
                    setSelectedInstallmentId(inst)
                    const sel = installments.find((i) => i.id === inst)
                    const factor = sel && totalAmount > 0 ? Number(sel.total) / Number(totalAmount) : 1
                    persistSelection({ installments: inst })
                    try {
                      const raw = localStorage.getItem('@CANNECT:CARD_BOLETO_SELECTION')
                      const prev = raw ? JSON.parse(raw) : {}
                      const next = { ...prev, feesFactor: factor }
                      localStorage.setItem('@CANNECT:CARD_BOLETO_SELECTION', JSON.stringify(next))
                    } catch {
                      /* ignore */
                    }
                  }}>
                  <option value="">Selecione a quantidade de parcelas</option>
                  {installments.map((it) => {
                    const fraction = totalAmount > 0 ? amountCardBRL / totalAmount : 0
                    const perInstallment = Number(it.amount) * fraction
                    const perTotal = Number(it.total) * fraction
                    const label = `${it.installment} de ${formatBRL(perInstallment)} ${
                      it.fees === '(sem juros)' ? '- sem juros' : `(total ${formatBRL(perTotal)})`
                    }`
                    return (
                      <option key={it.id} value={it.id}>
                        {label}
                      </option>
                    )
                  })}
                </select>
                <MdKeyboardArrowDown className="pointer-events-none absolute right-2 top-1/2 h-6 w-5 -translate-y-1/2 text-gray-500" />
              </div>
            </div>
          </div>

          {/* Valor a pagar - Cartão */}
          <div className="flex items-center justify-between border-t border-gray-200">
            <span className="text-sm text-neutral-800">Valor a pagar Cartão:</span>
            <span className="text-2xl font-semibold text-neutral-800">{formatBRL(amountCardBRL * cardFeesFactor)}</span>
          </div>
        </div>

        {showFormCard && (
          <CardAddForm
            onSubmit={handleAddCard}
            onCancel={() => setShowFormCard(false)}
            shippingAddress={shippingAddress}
          />
        )}

        {/* Boleto */}
        <div className="space-y-6 rounded-lg border border-gray-100 bg-gray-50 p-6">
          <h3 className="flex items-center gap-2 text-base font-semibold text-neutral-800">
            <BiBarcodeReader className="text-lg" />
            Boleto
          </h3>
          <hr
            style={{ borderTop: '0.2px solid #E3E3E3', borderLeft: 'none', borderRight: 'none', borderBottom: 'none' }}
          />

          {/* Porcentagem Boleto */}
          <div>
            <span className="mb-2 block text-sm text-neutral-800">Porcentagem do boleto</span>
            <div className="flex flex-wrap items-center gap-8">
              {[10, 25, 50, 75, 90].map((val) => (
                <button
                  key={val}
                  onClick={() => {
                    setPercentageBoleto(val)
                    setPercentageCard(100 - val)
                  }}
                  style={{ border: '1px solid #A2B156' }}
                  className={`inline-flex h-6 w-15 items-center justify-center gap-2.5 rounded-3xl px-4 py-1 text-base ${
                    percentageBoleto === val ? 'bg-[#A2B156] text-white' : 'bg-white text-[#A2B156] hover:bg-lime-100'
                  }`}>
                  {val}%
                </button>
              ))}
              <div
                style={{ border: '1px solid var(--Neutrals-Neutrals200, #CCCBCB)' }}
                className="ml-2 inline-flex h-6 w-23 items-center justify-center gap-2.5 rounded-3xl bg-[var(--Secondary-Secondary100,#EDECE7)] px-4 py-[6px] text-base text-neutral-500">
                {formatBRL(amountBoletoBRL)}
              </div>
            </div>
          </div>

          {/* Mensagem de geração do boleto apenas na confirmação */}
          <div className="rounded-lg bg-white p-4">
            <p className="text-sm text-neutral-700">O boleto será gerado após confirmar o pagamento.</p>
          </div>

          {/* Valor a pagar - Boleto */}
          <div className="flex items-center justify-between border-t border-gray-200">
            <span className="text-sm text-neutral-800">Valor a pagar Boleto:</span>
            <span className="text-2xl font-semibold text-neutral-800">{formatBRL(amountBoletoBRL)}</span>
          </div>
        </div>

        <div className="mt-6">
          <WarningBanners
            containsProductWithLongerDeliveryTime={false}
            containProductsMatirz={false}
            containsProductWithHubDistributor={false}
          />
        </div>
      </div>
    </div>
  )
}
